import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { formatDatePeru } from '@/utils/dateUtils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Plus, AlertTriangle } from 'lucide-react';
import { SupplierFormModal } from './components/SupplierFormModal';
import { SuppliersFilters } from './components/SuppliersFilters';
import { SuppliersTable } from './components/SuppliersTable';
import { suppliersService } from '@/services/suppliers.service';
import { toast } from 'sonner';
import type { Supplier, SupplierFilters, SupplierFormData } from '@/types/suppliers.types';

// Función para filtrar proveedores del lado del cliente
const filterSuppliers = (suppliers: Supplier[], filters: SupplierFilters): Supplier[] => {
  let filtered = suppliers;

  // Filtrar por búsqueda
  if (filters.search?.trim()) {
    const query = filters.search.toLowerCase();
    filtered = filtered.filter(supplier =>
      supplier.businessName.toLowerCase().includes(query) ||
      supplier.ruc.includes(query) ||
      supplier.primaryContact.name.toLowerCase().includes(query) ||
      supplier.address.city.toLowerCase().includes(query) ||
      supplier.address.region.toLowerCase().includes(query)
    );
  }

  // Filtrar por estado
  if (filters.isActive && filters.isActive !== 'all') {
    const isActive = filters.isActive === 'active';
    filtered = filtered.filter(supplier => supplier.isActive === isActive);
  }

  // Filtrar por ciudad
  if (filters.city && filters.city !== 'all') {
    filtered = filtered.filter(supplier => supplier.address.city === filters.city);
  }

  return filtered;
};

export const SuppliersPage: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const [allSuppliers, setAllSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [supplierToToggle, setSupplierToToggle] = useState<Supplier | null>(null);

  const [filters, setFilters] = useState<SupplierFilters>({
    search: '',
    isActive: 'all',
    city: 'all',
    sortBy: 'businessName',
    sortOrder: 'asc'
  });

  // Filtrar proveedores del lado del cliente (instantáneo)
  const suppliers = useMemo(() => {
    return filterSuppliers(allSuppliers, filters);
  }, [allSuppliers, filters]);

  // Cargar TODOS los proveedores una sola vez
  const loadSuppliers = useCallback(async () => {
    if (!isAuthenticated) {
      console.log('User not authenticated, skipping suppliers load');
      return;
    }

    setLoading(true);
    try {
      // Cargar todos los proveedores sin filtros
      const response = await suppliersService.getSuppliers({
        search: '',
        isActive: 'all',
        city: 'all',
        sortBy: 'businessName',
        sortOrder: 'asc',
        limit: 1000 // Cargar todos
      });

      if (response && response.success && Array.isArray(response.data)) {
        setAllSuppliers(response.data);
      } else {
        console.warn('Invalid suppliers response:', response);
        setAllSuppliers([]);
        toast.error('Respuesta inválida del servidor');
      }
    } catch (error) {
      console.error('Error loading suppliers:', error);
      setAllSuppliers([]);
      toast.error('Error al cargar proveedores');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Cargar proveedores solo una vez al montar el componente
  useEffect(() => {
    loadSuppliers();
  }, [loadSuppliers]);


  const handleEditSupplier = (supplier: Supplier) => {
    setEditingSupplier(supplier);
    setShowRegisterModal(true);
  };

  const handleSaveSupplier = async (formData: SupplierFormData) => {
    try {
      if (editingSupplier) {
        // Update existing supplier
        await suppliersService.updateSupplier(editingSupplier.id, formData);
        toast.success('Proveedor actualizado exitosamente');
      } else {
        // Create new supplier
        await suppliersService.createSupplier(formData);
        toast.success('Proveedor creado exitosamente');
      }
      setEditingSupplier(null);
      setShowRegisterModal(false);
      loadSuppliers();
    } catch (error) {
      console.error('Error saving supplier:', error);
      toast.error(editingSupplier ? 'Error al actualizar proveedor' : 'Error al crear proveedor');
    }
  };

  const handleNewSupplier = () => {
    setEditingSupplier(null);
    setShowRegisterModal(true);
  };



  const handleToggleSupplierStatus = (supplier: Supplier) => {
    setSupplierToToggle(supplier);
    setShowConfirmDialog(true);
  };

  const handleConfirmToggleStatus = async () => {
    if (!supplierToToggle) return;

    try {
      if (supplierToToggle.isActive) {
        // Desactivar proveedor
        await suppliersService.deleteSupplier(supplierToToggle.id);
        toast.success('Proveedor desactivado exitosamente');
      } else {
        // Reactivar proveedor - actualizamos con isActive: true
        await suppliersService.updateSupplier(supplierToToggle.id, { isActive: true });
        toast.success('Proveedor reactivado exitosamente');
      }

      setShowConfirmDialog(false);
      setSupplierToToggle(null);
      loadSuppliers();
    } catch (error) {
      console.error('Error toggling supplier status:', error);
      toast.error('Error al cambiar el estado del proveedor');
    }
  };

  const handleCancelToggleStatus = () => {
    setShowConfirmDialog(false);
    setSupplierToToggle(null);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'PEN'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return formatDatePeru(dateString);
  };

  // Show loading state while auth is being checked
  if (isLoading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rose-600"></div>
          <p className="text-gray-600 ml-3">Cargando...</p>
        </div>
      </div>
    );
  }

  // Show authentication required message
  if (!isAuthenticated) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Autenticación requerida</h2>
            <p className="text-gray-600">Debes iniciar sesión para acceder a la gestión de proveedores.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 md:p-6 space-y-4 md:space-y-6 h-full overflow-auto">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Gestión de Proveedores</h1>
          <p className="text-gray-600 mt-1 text-sm md:text-base">
            Administra la información de tus proveedores y sus productos
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <Button
            onClick={handleNewSupplier}
            className="flex items-center gap-2 bg-macartur-primary hover:bg-macartur-primary/90"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Nuevo Proveedor</span>
          </Button>
        </div>
      </div>


      {/* Filters */}
      <SuppliersFilters
        filters={filters}
        onFiltersChange={(newFilters) => setFilters(prev => ({ ...prev, ...newFilters }))}
      />

      {/* Suppliers Table */}
      <div className="bg-white rounded-lg border">
        <SuppliersTable
          suppliers={suppliers}
          loading={loading}
          onEdit={handleEditSupplier}
          onToggleStatus={handleToggleSupplierStatus}
          formatCurrency={formatCurrency}
          formatDate={formatDate}
        />
      </div>


      {/* Register/Edit Supplier Modal */}
      <SupplierFormModal
        open={showRegisterModal}
        onOpenChange={setShowRegisterModal}
        supplier={editingSupplier || undefined}
        onSave={handleSaveSupplier}
      />


      {/* Confirmation Dialog for Status Toggle */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              {supplierToToggle?.isActive ? 'Desactivar Proveedor' : 'Reactivar Proveedor'}
            </DialogTitle>
            <DialogDescription>
              {supplierToToggle?.isActive ? (
                <>
                  ¿Estás seguro de que deseas desactivar el proveedor{' '}
                  <strong>{supplierToToggle?.businessName}</strong>?
                  <br />
                  <br />
                  El proveedor será marcado como inactivo y no aparecerá en las búsquedas por defecto.
                  Podrás reactivarlo más tarde si es necesario.
                </>
              ) : (
                <>
                  ¿Estás seguro de que deseas reactivar el proveedor{' '}
                  <strong>{supplierToToggle?.businessName}</strong>?
                  <br />
                  <br />
                  El proveedor volverá a estar disponible para nuevas órdenes y aparecerá en las búsquedas.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancelToggleStatus}>
              Cancelar
            </Button>
            <Button
              variant={supplierToToggle?.isActive ? "destructive" : "default"}
              onClick={handleConfirmToggleStatus}
            >
              {supplierToToggle?.isActive ? 'Desactivar' : 'Reactivar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
